import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as ContractIcon
} from '@mui/icons-material';
import contractTypeService from '../services/contractTypeService';
import { useConfirm } from './Common/ConfirmDialog';

const ContractTypeManagement = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [contractTypes, setContractTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: ''
  });

  useEffect(() => {
    loadContractTypes();
  }, []);

  const loadContractTypes = async () => {
    try {
      setLoading(true);
      const data = await contractTypeService.getAll();
      setContractTypes(data);
      setError('');
    } catch (err) {
      setError('Error loading contract types: ' + err.message);
      console.error('Error loading contract types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ code: '', name: '', description: '' });
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code,
      name: item.name,
      description: item.description || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ message: 'Are you sure you want to delete this contract type?', danger: true, confirmLabel: 'Delete' }))) {
      return;
    }

    try {
      await contractTypeService.delete(id);
      await loadContractTypes();
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data || err.message || 'Failed to delete contract type';
      setError(errorMessage);
      console.error('Error deleting contract type:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingItem) {
        await contractTypeService.update(editingItem.id, formData);
      } else {
        await contractTypeService.create(formData);
      }

      setOpenDialog(false);
      await loadContractTypes();
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data || err.message || 'Failed to save contract type';
      setError(errorMessage);
      console.error('Error saving contract type:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="position-management-container">
      {/* Header with Breadcrumbs */}
      <Box className="position-header">
        <Breadcrumbs className="breadcrumbs">
          <Link
            color="inherit"
            onClick={() => navigate('/settings')}
            className="breadcrumb-link"
          >
            Settings
          </Link>
          <Typography color="text.primary">Contract Types</Typography>
        </Breadcrumbs>

        <Box className="header-content">
          <Box className="title-section">
            <ContractIcon className="header-icon" />
            <Box>
              <Typography variant="h4" className="page-title">
                Contract Types
              </Typography>
              <Typography variant="body1" className="page-subtitle">
                Manage contract types and their configurations
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            className="add-button"
          >
            Add Contract Type
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} className="alert">
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Contracts</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contractTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No contract types found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              contractTypes.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#d32f2f' }}>
                      {item.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {item.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      {item.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.contractCount || 0}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(item)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Contract Type' : 'Add New Contract Type'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
              margin="normal"
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ContractTypeManagement;
